import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import Layout from './components/Layout'
import analytics from './utils/analytics'

const Home = lazy(() => import('./pages/Home'))
const Services = lazy(() => import('./pages/Services'))
const Solutions = lazy(() => import('./pages/Solutions'))
const About = lazy(() => import('./pages/About'))
const Contact = lazy(() => import('./pages/Contact'))
const Sectors = lazy(() => import('./pages/Sectors'))
const Partners = lazy(() => import('./pages/Partners'))
const Blog = lazy(() => import('./pages/Blog'))
const Careers = lazy(() => import('./pages/Careers'))
const News = lazy(() => import('./pages/News'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfUse = lazy(() => import('./pages/TermsOfUse'))
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'))

const FutureEnterpriseAI = lazy(() => import('./pages/blog/FutureEnterpriseAI'))
const AIConciergeServices = lazy(() => import('./pages/blog/AIConciergeServices'))
const EnterpriseAIDeployments = lazy(() => import('./pages/blog/EnterpriseAIDeployments'))
const CloudContactCenters = lazy(() => import('./pages/blog/CloudContactCenters'))
const VMwareCustomAppliance = lazy(() => import('./pages/blog/VMwareCustomAppliance'))
const Windows365Link = lazy(() => import('./pages/blog/Windows365Link'))
const Windows10EOL = lazy(() => import('./pages/blog/Windows10EOL'))
const CloudMigrationBestPractices = lazy(() => import('./pages/blog/CloudMigrationBestPractices'))
const CybersecurityRemoteWork = lazy(() => import('./pages/blog/CybersecurityRemoteWork'))
const DigitalTransformationROI = lazy(() => import('./pages/blog/DigitalTransformationROI'))

const SeniorCloudSolutionsArchitect = lazy(() => import('./pages/careers/SeniorCloudSolutionsArchitect'))
const CybersecurityConsultant = lazy(() => import('./pages/careers/CybersecurityConsultant'))
const AIMLSolutionsEngineer = lazy(() => import('./pages/careers/AIMLSolutionsEngineer'))
const DevOpsEngineer = lazy(() => import('./pages/careers/DevOpsEngineer'))
const BusinessAnalyst = lazy(() => import('./pages/careers/BusinessAnalyst'))
const TechnicalProjectManager = lazy(() => import('./pages/careers/TechnicalProjectManager'))
const NOCEngineer = lazy(() => import('./pages/careers/NOCEngineer'))
const SupportEngineer = lazy(() => import('./pages/careers/SupportEngineer'))
const HelpDeskOperator = lazy(() => import('./pages/careers/HelpDeskOperator'))
const RelationshipManager = lazy(() => import('./pages/careers/RelationshipManager'))

const Finance = lazy(() => import('./pages/sectors/Finance'))
const Manufacturing = lazy(() => import('./pages/sectors/Manufacturing'))
const Healthcare = lazy(() => import('./pages/sectors/Healthcare'))
const HighNetWorth = lazy(() => import('./pages/sectors/HighNetWorth'))
const ProfessionalServices = lazy(() => import('./pages/sectors/ProfessionalServices'))
const Startups = lazy(() => import('./pages/sectors/Startups'))

const ApplicationRefactoring = lazy(() => import('./pages/services/ApplicationRefactoring'))
const DevelopmentServices = lazy(() => import('./pages/services/DevelopmentServices'))
const ManagedDesktops = lazy(() => import('./pages/services/ManagedDesktops'))
const ManagedHelpDesk = lazy(() => import('./pages/services/ManagedHelpDesk'))
const ManagedSecurity = lazy(() => import('./pages/services/ManagedSecurity'))
const NetworkOperations = lazy(() => import('./pages/services/NetworkOperations'))
const OpenSourceSupport = lazy(() => import('./pages/services/OpenSourceSupport'))
const ProjectRecovery = lazy(() => import('./pages/services/ProjectRecovery'))
const VibeCheck = lazy(() => import('./pages/services/VibeCheck'))
const Quote = lazy(() => import('./pages/services/Quote'))

const BusinessProcessAutomation = lazy(() => import('./pages/solutions/BusinessProcessAutomation'))
const SecureDesktop = lazy(() => import('./pages/solutions/SecureDesktop'))
const EmailSecurity = lazy(() => import('./pages/solutions/EmailSecurity'))
const EnterpriseSecurity = lazy(() => import('./pages/solutions/EnterpriseSecurity'))
const InfrastructureAutomation = lazy(() => import('./pages/solutions/InfrastructureAutomation'))
const MainframeMigration = lazy(() => import('./pages/solutions/MainframeMigration'))
const VirtualBench = lazy(() => import('./pages/solutions/VirtualBench'))
const VMwareReplatforming = lazy(() => import('./pages/solutions/VMwareReplatforming'))

const OnePassword = lazy(() => import('./pages/partners/OnePassword'))
const Adobe = lazy(() => import('./pages/partners/Adobe'))
const Anthropic = lazy(() => import('./pages/partners/Anthropic'))
const Apple = lazy(() => import('./pages/partners/Apple'))
const AutoMox = lazy(() => import('./pages/partners/AutoMox'))
const AWS = lazy(() => import('./pages/partners/AWS'))
const Cisco = lazy(() => import('./pages/partners/Cisco'))
const Citrix = lazy(() => import('./pages/partners/Citrix'))
const CrowdStrike = lazy(() => import('./pages/partners/CrowdStrike'))
const DarkTrace = lazy(() => import('./pages/partners/DarkTrace'))
const DellEMC = lazy(() => import('./pages/partners/DellEMC'))
const FreshWorks = lazy(() => import('./pages/partners/FreshWorks'))
const Google = lazy(() => import('./pages/partners/Google'))
const HPE = lazy(() => import('./pages/partners/HPE'))
const IBM = lazy(() => import('./pages/partners/IBM'))
const Microsoft = lazy(() => import('./pages/partners/Microsoft'))
const Morpheus = lazy(() => import('./pages/partners/Morpheus'))
const N8N = lazy(() => import('./pages/partners/N8N'))
const NetApp = lazy(() => import('./pages/partners/NetApp'))
const Nutanix = lazy(() => import('./pages/partners/Nutanix'))
const NVidia = lazy(() => import('./pages/partners/NVidia'))
const OpenAI = lazy(() => import('./pages/partners/OpenAI'))
const Oracle = lazy(() => import('./pages/partners/Oracle'))
const PaloAltoNetworks = lazy(() => import('./pages/partners/PaloAltoNetworks'))
const SAP = lazy(() => import('./pages/partners/SAP'))
const SentinelOne = lazy(() => import('./pages/partners/SentinelOne'))
const ServiceNOW = lazy(() => import('./pages/partners/ServiceNOW'))
const SolarWinds = lazy(() => import('./pages/partners/SolarWinds'))
const SonicWall = lazy(() => import('./pages/partners/SonicWall'))
const Tenable = lazy(() => import('./pages/partners/Tenable'))
const Vast = lazy(() => import('./pages/partners/Vast'))
const VMware = lazy(() => import('./pages/partners/VMware'))
const Weka = lazy(() => import('./pages/partners/Weka'))
const Workato = lazy(() => import('./pages/partners/Workato'))
const XAI = lazy(() => import('./pages/partners/XAI'))

function App() {
  const location = useLocation();

  useEffect(() => {
    analytics.trackPageView(location.pathname);
  }, [location]);

  return (
    <Layout>
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div></div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/index.html" element={<Navigate to="/" replace />} />

          {/* Sectors routes */}
          <Route path="/sectors" element={<Sectors />} />
          <Route path="/sectors/finance" element={<Finance />} />
          <Route path="/sectors/manufacturing" element={<Manufacturing />} />
          <Route path="/sectors/healthcare" element={<Healthcare />} />
          <Route path="/sectors/high-net-worth" element={<HighNetWorth />} />
          <Route path="/sectors/professional-services" element={<ProfessionalServices />} />
          <Route path="/sectors/startups" element={<Startups />} />

          {/* Services routes */}
          <Route path="/services.html" element={<Navigate to="/services" replace />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/application-refactoring" element={<ApplicationRefactoring />} />
          <Route path="/services/development-services" element={<DevelopmentServices />} />
          <Route path="/services/managed-desktops" element={<ManagedDesktops />} />
          <Route path="/services/managed-help-desk" element={<ManagedHelpDesk />} />
          <Route path="/services/managed-security" element={<ManagedSecurity />} />
          <Route path="/services/network-operations" element={<NetworkOperations />} />
          <Route path="/services/open-source-support" element={<OpenSourceSupport />} />
          <Route path="/services/project-recovery" element={<ProjectRecovery />} />
          <Route path="/services/vibe-check" element={<VibeCheck />} />
          <Route path="/services/quote" element={<Quote />} />

          {/* Solutions routes */}
          <Route path="/solutions.html" element={<Navigate to="/solutions" replace />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/solutions/business-process-automation" element={<BusinessProcessAutomation />} />
          <Route path="/solutions/secure-desktop" element={<SecureDesktop />} />
          <Route path="/solutions/email-security" element={<EmailSecurity />} />
          <Route path="/solutions/enterprise-security" element={<EnterpriseSecurity />} />
          <Route path="/solutions/infrastructure-automation" element={<InfrastructureAutomation />} />
          <Route path="/solutions/mainframe-migration" element={<MainframeMigration />} />
          <Route path="/solutions/virtual-bench" element={<VirtualBench />} />
          <Route path="/solutions/vmware-replatforming" element={<VMwareReplatforming />} />

          {/* Other routes */}
          <Route path="/aboutus.html" element={<Navigate to="/about" replace />} />
          <Route path="/about" element={<About />} />
          <Route path="/contactus.html" element={<Navigate to="/contact" replace />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/20260330-windows-365-link" element={<Windows365Link />} />
          <Route path="/blog/20260329-enterprise-ai-deployments" element={<EnterpriseAIDeployments />} />
          <Route path="/blog/20260313-ai-concierge-services" element={<AIConciergeServices />} />
          <Route path="/blog/20260115-future-enterprise-ai" element={<FutureEnterpriseAI />} />
          <Route path="/blog/20260112-cloud-migration" element={<CloudMigrationBestPractices />} />
          <Route path="/blog/20260110-cybersecurity-remote" element={<CybersecurityRemoteWork />} />
          <Route path="/blog/20260108-digital-transformation-roi" element={<DigitalTransformationROI />} />
          <Route path="/blog/20251020-cloud-contact-centers" element={<CloudContactCenters />} />
          <Route path="/blog/20250905-windows-10-eol" element={<Windows10EOL />} />
          <Route path="/blog/20240301-vmware-custom-appliance" element={<VMwareCustomAppliance />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/careers/1" element={<SeniorCloudSolutionsArchitect />} />
          <Route path="/careers/2" element={<CybersecurityConsultant />} />
          <Route path="/careers/3" element={<AIMLSolutionsEngineer />} />
          <Route path="/careers/4" element={<DevOpsEngineer />} />
          <Route path="/careers/5" element={<BusinessAnalyst />} />
          <Route path="/careers/6" element={<TechnicalProjectManager />} />
          <Route path="/careers/7" element={<NOCEngineer />} />
          <Route path="/careers/8" element={<SupportEngineer />} />
          <Route path="/careers/9" element={<HelpDeskOperator />} />
          <Route path="/careers/10" element={<RelationshipManager />} />
          <Route path="/news" element={<News />} />

          {/* Legal/Policy routes */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />

          {/* Partners routes */}
          <Route path="/partners" element={<Partners />} />
          <Route path="/partners/1password" element={<OnePassword />} />
          <Route path="/partners/adobe" element={<Adobe />} />
          <Route path="/partners/anthropic" element={<Anthropic />} />
          <Route path="/partners/apple" element={<Apple />} />
          <Route path="/partners/automox" element={<AutoMox />} />
          <Route path="/partners/aws" element={<AWS />} />
          <Route path="/partners/cisco" element={<Cisco />} />
          <Route path="/partners/citrix" element={<Citrix />} />
          <Route path="/partners/crowdstrike" element={<CrowdStrike />} />
          <Route path="/partners/darktrace" element={<DarkTrace />} />
          <Route path="/partners/dell-emc" element={<DellEMC />} />
          <Route path="/partners/freshworks" element={<FreshWorks />} />
          <Route path="/partners/google" element={<Google />} />
          <Route path="/partners/hpe" element={<HPE />} />
          <Route path="/partners/ibm" element={<IBM />} />
          <Route path="/partners/microsoft" element={<Microsoft />} />
          <Route path="/partners/morpheus" element={<Morpheus />} />
          <Route path="/partners/n8n" element={<N8N />} />
          <Route path="/partners/netapp" element={<NetApp />} />
          <Route path="/partners/nutanix" element={<Nutanix />} />
          <Route path="/partners/nvidia" element={<NVidia />} />
          <Route path="/partners/openai" element={<OpenAI />} />
          <Route path="/partners/oracle" element={<Oracle />} />
          <Route path="/partners/palo-alto-networks" element={<PaloAltoNetworks />} />
          <Route path="/partners/sap" element={<SAP />} />
          <Route path="/partners/sentinelone" element={<SentinelOne />} />
          <Route path="/partners/servicenow" element={<ServiceNOW />} />
          <Route path="/partners/solarwinds" element={<SolarWinds />} />
          <Route path="/partners/sonicwall" element={<SonicWall />} />
          <Route path="/partners/tenable" element={<Tenable />} />
          <Route path="/partners/vast" element={<Vast />} />
          <Route path="/partners/vmware" element={<VMware />} />
          <Route path="/partners/weka" element={<Weka />} />
          <Route path="/partners/workato" element={<Workato />} />
          <Route path="/partners/xai" element={<XAI />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}

export default App